import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Link } from 'react-router-dom';
import {
  ShoppingCart, Calendar, AlertTriangle, ListTodo, ArrowRight,
} from 'lucide-react';
import { fmtDate } from '@/lib/utils';

// Live at-a-glance strip for the admin dashboard: today's KPIs plus the most
// recent orders/bookings and any overdue CRM follow-ups, so the admin lands on
// a single screen that shows what needs attention before drilling in.

function Kpi({ icon: Icon, label, value, accent, to, attention }) {
  return (
    <Link
      to={to}
      className={`group flex items-center gap-3 border bg-[#111115] hover:bg-[#151519] p-4 transition-all ${
        attention === 'red'
          ? 'attention-red'
          : attention === 'amber'
          ? 'attention-amber'
          : 'border-white/10 hover:border-primary/40'
      }`}
    >
      <span className={`w-9 h-9 flex items-center justify-center border ${accent}`}>
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <p className="text-white font-black text-xl leading-none">{value}</p>
        <p className="text-white/30 text-[9px] font-bold tracking-widest mt-1">{label}</p>
      </div>
    </Link>
  );
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function DashboardLiveOverview() {
  const { data: orders = [] } = useQuery({
    queryKey: ['dash-recent-orders'],
    queryFn: () => base44.entities.Order.list('-created_date', 50),
  });
  const { data: bookings = [] } = useQuery({
    queryKey: ['dash-recent-bookings'],
    queryFn: () => base44.entities.Booking.list('-created_date', 50),
  });
  const { data: tasks = [] } = useQuery({
    queryKey: ['dash-crm-tasks'],
    queryFn: () => base44.entities.CustomerTask.list('due_date', 200),
  });

  const today = todayISO();
  const ordersToday = orders.filter((o) => (o.created_date || '').slice(0, 10) === today).length;
  const bookingsToday = bookings.filter((b) => (b.created_date || '').slice(0, 10) === today).length;
  const pendingOrders = orders.filter((o) => o.status === 'pending' || o.status === 'paid').length;

  const now = new Date(new Date().toDateString());
  const openTasks = tasks.filter((t) => t.status === 'pending');
  const overdue = openTasks.filter((t) => t.due_date && new Date(t.due_date) < now);

  const recentOrders = orders.slice(0, 5);
  const recentBookings = bookings.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={ShoppingCart} label="ORDERS TODAY" value={ordersToday} accent="bg-primary/10 border-primary/20 text-primary" to="/admin/orders" />
        <Kpi icon={Calendar} label="BOOKINGS TODAY" value={bookingsToday} accent="bg-blue-500/10 border-blue-500/20 text-blue-400" to="/admin/bookings" />
        <Kpi icon={ListTodo} label="OPEN ORDERS" value={pendingOrders} accent="bg-amber-500/10 border-amber-500/20 text-amber-400" to="/admin/orders" attention={pendingOrders > 0 ? 'amber' : null} />
        <Kpi icon={AlertTriangle} label="OVERDUE TASKS" value={overdue.length} accent="bg-red-500/10 border-red-500/20 text-red-400" to="/admin/crm" attention={overdue.length > 0 ? 'red' : null} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent orders */}
        <Panel
          title="RECENT ORDERS"
          to="/admin/orders"
          rows={recentOrders}
          empty="No orders yet."
          render={(o) => (
            <div className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-0">
              <div className="min-w-0">
                <p className="text-white text-xs font-medium truncate">{o.customer_name || '—'}</p>
                <p className="text-white/30 text-[10px] truncate">{o.product_name || '—'} · {fmtDate(o.created_date)}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-primary font-black text-xs">£{Number(o.amount || 0).toFixed(2)}</p>
                <p className="text-white/30 text-[9px] font-bold tracking-widest uppercase">{(o.status || '').replace('_', ' ')}</p>
              </div>
            </div>
          )}
        />

        {/* Recent bookings */}
        <Panel
          title="RECENT BOOKINGS"
          to="/admin/bookings"
          rows={recentBookings}
          empty="No bookings yet."
          render={(b) => (
            <div className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-0">
              <div className="min-w-0">
                <p className="text-white text-xs font-medium truncate">{b.customer_name || '—'}</p>
                <p className="text-white/30 text-[10px] truncate">
                  {(b.experience_type || '—').replace(/_/g, ' ')} · {fmtDate(b.date) || '—'}
                </p>
              </div>
              <span className={`text-[9px] font-bold tracking-widest uppercase shrink-0 ${
                b.status === 'confirmed' ? 'text-primary' :
                b.status === 'completed' ? 'text-green-400' :
                b.status === 'cancelled' ? 'text-red-400' : 'text-amber-400'
              }`}>
                {b.status || 'pending'}
              </span>
            </div>
          )}
        />

        {/* CRM follow-ups */}
        <Panel
          title="FOLLOW-UPS DUE"
          to="/admin/crm"
          rows={[...overdue, ...openTasks.filter((t) => !overdue.includes(t))].slice(0, 6)}
          empty="No open tasks. You're all caught up."
          attention={overdue.length > 0 ? 'red' : null}
          render={(t) => {
            const isOverdue = overdue.includes(t);
            return (
              <div className={`flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-0 ${isOverdue ? 'border-l-2 border-l-red-500 pl-2' : ''}`}>
                <div className="min-w-0">
                  <p className="text-white text-xs font-medium truncate">{t.title}</p>
                  <p className="text-white/30 text-[10px] truncate">{t.customer_name || t.customer_email || '—'}</p>
                </div>
                <span className={`text-[9px] font-bold tracking-widest shrink-0 ${isOverdue ? 'text-red-400 animate-pulse' : 'text-white/40'}`}>
                  {t.due_date ? fmtDate(t.due_date) : 'NO DATE'}{isOverdue && ' · LATE'}
                </span>
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}

function Panel({ title, to, rows, empty, render, attention }) {
  return (
    <div className={`border bg-[#111115] p-4 ${attention === 'red' ? 'attention-red' : 'border-white/10'}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-white/40 text-[10px] font-bold tracking-widest">{title}</p>
        <Link to={to} className="flex items-center gap-1 text-primary/70 hover:text-primary text-[10px] font-bold tracking-widest transition-colors">
          VIEW ALL <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      {rows.length === 0 ? (
        <p className="text-white/20 text-xs py-6 text-center">{empty}</p>
      ) : (
        <div>{rows.map((r) => render(r))}</div>
      )}
    </div>
  );
}